import type {
  PropertiesData,
  PropertyDeclaration,
  StateDefinition,
  TransformOverride,
  TransitionConfig,
  TimeTransition,
} from '../../scenedata/behaviors/propertiesdata';
import type { PropertyValue } from '../../behaviors/properties';

// ---------------------------------------------------------------------------
// State-group data model — pure ops on PropertiesData.
//
// Staged into trace-viewer/editor from TraceFrontend (statesModel.ts) as the
// first step of the state-group centralization. These functions
// are pure: they take PropertiesData and return new PropertiesData (or the input
// unchanged), with no SceneObject access, side effects, or undo coupling.
//
// "Base is not a state" (STATES_EDITTIME_RULES_SPEC.md P4): Base is the absence
// of an active override — the asset's authored data — NOT a stored `states[]`
// entry. The model therefore does NOT seed an empty Base entry; switching to Base
// is `setState(undefined)` at runtime, or an editor pick of `null`
// (`setEditorState`): the resolver falls back to authored declared values. `BASE_STATE_NAME` survives only as an
// editor LABEL for that no-override card.
// ---------------------------------------------------------------------------

/**
 * Editor label for the "no override" (Base) card. NOT a stored state key — Base
 * is the absence of an active state, resolved from authored data. Kept for UI
 * display only; do not write it into `states`.
 */
export const BASE_STATE_NAME = 'Base State';

/**
 * Default transition applied to a newly-created state, so switching to it
 * animates out of the box instead of snapping. Matches the pseudo-state default
 * used elsewhere in the editor (0.3s, easeOut). Creators can edit or clear it.
 * A fresh object each call so callers can spread it without aliasing.
 */
export function defaultStateTransition(): TimeTransition {
  return { duration: 0.3, easing: 'easeOut' };
}

// ---------------------------------------------------------------------------
// baseStateName / isBaseState
// ---------------------------------------------------------------------------

/**
 * Base is NOT a stored state — it is the absence of an active override (the
 * asset's authored data), reached via `setState(undefined)`. There is therefore
 * normally no "base state name": this returns `undefined`.
 *
 * The sole exception is an explicit pin: a creator may set `initialState` to a
 * real state to make THAT the on-load default instead of Base. When
 * `initialState` points at a real state, that name is the effective load-time
 * base and is returned; a stale/absent `initialState` means Base (undefined).
 *
 * (Historically this fell back to the first state key, because an empty
 * `BASE_STATE_NAME` entry was always seeded as slot 0. With Base implicit there
 * is no such entry, so there is no first-key fallback — that would wrongly treat
 * a real authored state as Base.)
 */
export function baseStateName(data: PropertiesData): string | undefined {
  const states = data.states ?? {};
  const init = data.initialState;
  if (init !== undefined && init in states) return init;
  return undefined;
}

/**
 * True when `name` is the effective base for `data` — i.e. it is the explicitly
 * pinned `initialState`. With Base implicit, an unpinned asset has no base state
 * name, so this is false for every real state (Base is `undefined`, not a name).
 */
export function isBaseState(data: PropertiesData, name: string): boolean {
  return name === baseStateName(data);
}

// ---------------------------------------------------------------------------
// stateHasOverrides
// ---------------------------------------------------------------------------

/**
 * Whether a state definition carries ANY field at all (`values`, `transform`,
 * `transition`, `active`, `enterFrom`) versus being a completely empty placeholder.
 *
 * ⚠️ This returns `true` for the auto-applied DEFAULT-TRANSITION scaffold: every
 * state created since the baked-transition change carries `transition`, so a fresh
 * seed satisfies this even with no user-authored content. For the precise "does
 * this carry a REAL authored override (not just scaffolding)?" question — what
 * ungroup/validation use to prune — call {@link stateIsDefaultSeed} instead (it
 * excludes a default transition ± a redundant keyed pose). Consumers writing an
 * "is this state empty/unmodified" check (e.g. card hydration) should prefer
 * `!stateIsDefaultSeed`, not `!stateHasOverrides`.
 *
 * Must account for EVERY overridable field on `StateDefinition` — a state whose
 * ONLY content is `{ active: false }` or `{ enterFrom: ... }` is real. Keep in
 * lockstep with the fields `cloneState` copies.
 */
export function stateHasOverrides(def: StateDefinition | undefined): boolean {
  if (!def) return false;
  return (
    (def.values !== undefined && Object.keys(def.values).length > 0) ||
    def.transform !== undefined ||
    def.transition !== undefined ||
    def.active !== undefined ||
    def.enterFrom !== undefined
  );
}

/**
 * Whether `t` is (structurally) the auto-applied {@link defaultStateTransition} —
 * i.e. scaffolding the editor adds on creation, not something the creator hand-
 * authored. A `null`/`undefined` transition is treated as "default scaffolding"
 * (no transition is also nothing the user authored). Any transition that DIFFERS
 * from the default (custom duration/easing/delay, or a property-driven transition)
 * is a real authored override.
 *
 * Compares fields explicitly (not via JSON.stringify, which is property-order
 * sensitive — `{easing, duration}` would falsely read as non-default). The default
 * is a TIME transition, so any property-driven (`driver`) transition is custom. A
 * missing `delay` is equivalent to `delay: 0` (the data-model default).
 */
export function isDefaultTransition(t: TransitionConfig | undefined): boolean {
  if (t === undefined) return true;
  if ('driver' in t) return false; // property-driven → never the (time-based) default
  // `t` is now narrowed to TimeTransition; `defaultStateTransition()` returns one.
  const d = defaultStateTransition();
  return t.duration === d.duration && t.easing === d.easing && (t.delay ?? 0) === (d.delay ?? 0);
}

/**
 * Whether a state carries no MEANINGFUL authored override beyond the auto-applied
 * default scaffolding — i.e. its only content is a `transform` and/or a keyed
 * `active` and/or a transition THAT EQUALS THE DEFAULT, with no `values`, no
 * `enterFrom`, and no CUSTOM transition.
 *
 * This is the shape every creation path now produces for a fresh/seeded member: a
 * default transition (from `addState`) ± a keyed live pose ± a keyed live
 * visibility (from the subtree-key on add). All are auto-applied scaffolding; the
 * keyed pose/visibility are only meaningful if they DIFFER from the authored base
 * (the caller checks that — see `dropStatesOnUngroup`, which compares both against
 * authored before pruning). A state that overrides values / enterFrom — OR carries
 * a HAND-CUSTOMIZED transition — is a real override and is kept.
 *
 * (Formerly `stateIsKeyedPoseOnly`, which required a transform; widened to also
 * match a transition-only seed, then narrowed to exclude custom transitions so an
 * authored transition isn't mistaken for scaffolding, then widened again for the
 * keyed `active` once add-state started pinning member visibility.)
 */
export function stateIsDefaultSeed(def: StateDefinition | undefined): boolean {
  if (!def) return false;
  const hasValues = def.values !== undefined && Object.keys(def.values).length > 0;
  if (hasValues || def.enterFrom !== undefined) return false;
  // A custom transition is a real authored override — not scaffolding.
  if (!isDefaultTransition(def.transition)) return false;
  // Only a (default) transition and/or a keyed transform/active remains →
  // default scaffolding (redundancy vs authored is the caller's comparison).
  return true;
}

// ---------------------------------------------------------------------------
// nextStateName
// ---------------------------------------------------------------------------

/**
 * Returns "State 1" if no "State N" slot is taken, otherwise the first free
 * "State N" (N >= 1).
 */
export function nextStateName(states: Record<string, StateDefinition>): string {
  for (let n = 1; ; n++) {
    const candidate = `State ${n}`;
    if (!(candidate in states)) return candidate;
  }
}

// ---------------------------------------------------------------------------
// addState
// ---------------------------------------------------------------------------

/**
 * Appends a new state with empty values and, by default, the {@link
 * defaultStateTransition} — so EVERY creation path (owner, member fan-out, clone,
 * panel) animates consistently without each caller remembering to add it. Pass
 * `{ transition: null }` for a pure placeholder seed with no transition (rare; the
 * member-seed fan-out used to rely on this, but now seeds intentionally carry the
 * default transition too — see `seedMemberStatesFromGroup`). Returns the new data
 * and the resolved name.
 *
 * A lone default transition is NOT a meaningful authored override: ungroup prunes
 * a state whose only content is a (default) transition ± a keyed-authored pose
 * (see `stateIsDefaultSeed` / `dropStatesOnUngroup`).
 *
 * Does NOT touch `initialState`: with Base implicit, the on-load default stays
 * Base (no override) unless a creator explicitly pins a state.
 */
export function addState(
  data: PropertiesData,
  name?: string,
  opts: { transition?: TransitionConfig | null } = {},
): { data: PropertiesData; name: string } {
  const existingStates = data.states ?? {};
  const resolvedName = name ?? nextStateName(existingStates);

  const transition = opts.transition === undefined ? defaultStateTransition() : opts.transition;
  const newState: StateDefinition = {
    values: {},
    ...(transition ? { transition } : {}),
  };

  const next: PropertiesData = {
    ...data,
    states: { ...existingStates, [resolvedName]: newState },
  };

  return { data: next, name: resolvedName };
}

/**
 * Add a state whose transition EXACTLY mirrors `transition` — including a cleared
 * (`undefined` = instant snap) one. Encapsulates the "suppress addState's baked
 * default, then apply the source's actual transition" two-step that fan-out paths
 * need so a member animates identically to its owner/source. Using plain `addState`
 * for a member would silently bake the 0.3s default where the source SNAPS; this
 * helper makes that contract enforced rather than re-explained per call site.
 *
 * (Distinct from `addState(data, name)`, which DEFAULTS the transition for a
 * brand-new authored state. Use this only when matching an existing state's.)
 */
export function addStateMirroringTransition(
  data: PropertiesData,
  name: string,
  transition: TransitionConfig | undefined,
): PropertiesData {
  // `transition: null` suppresses the baked default; then set the exact value.
  const seeded = addState(data, name, { transition: null }).data;
  return setStateTransition(seeded, name, transition);
}

// ---------------------------------------------------------------------------
// addInitialStates
// ---------------------------------------------------------------------------

/**
 * Seeds the first real state. With "Base is not a state", the baseline is
 * implicit — the asset's authored data, reachable by clearing the active state
 * (`setState(undefined)`) — so no Base entry is stored. We add a single
 * modifiable "State 1" and leave `initialState` UNSET, so the on-load default is
 * Base (the unmodified look). Returns the new data and the modifiable state name
 * (the caller activates it so edits land on it rather than on Base).
 *
 * (Historically this seeded TWO entries — an empty `BASE_STATE_NAME` baseline
 * plus a modifiable state — because Base had to be a switch-back target. Base is
 * now implicit, so the baseline entry is gone.)
 */
export function addInitialStates(
  data: PropertiesData,
): { data: PropertiesData; name: string } {
  // Delegates to addState so the first state gets the same default transition as
  // every other creation path. initialState left unset → Base is the load default.
  return addState(data);
}

// ---------------------------------------------------------------------------
// cloneState
// ---------------------------------------------------------------------------

/**
 * Deep-copies `values`, `transform`, and `transition` from `fromName` into a
 * new state named `newName` (auto-generated if omitted).
 *
 * Edge case: if `fromName` is not present in `data.states`, returns
 * `{ data, name: fromName }` (input unchanged, caller's name echoed back).
 */
export function cloneState(
  data: PropertiesData,
  fromName: string,
  newName?: string,
): { data: PropertiesData; name: string } {
  const existingStates = data.states ?? {};

  if (!(fromName in existingStates)) {
    return { data, name: fromName };
  }

  const source = existingStates[fromName];
  const resolvedName = newName ?? nextStateName(existingStates);

  const cloned: StateDefinition = {
    // `?? {}` so a source missing `values` clones to `{}` (what addState produces),
    // not `values: undefined` — keeps the serialized shape consistent.
    values: structuredClone(source.values ?? {}),
    ...(source.transform !== undefined ? { transform: structuredClone(source.transform) } : {}),
    ...(source.active !== undefined ? { active: source.active } : {}),
    ...(source.enterFrom !== undefined ? { enterFrom: structuredClone(source.enterFrom) } : {}),
    ...(source.transition !== undefined ? { transition: structuredClone(source.transition) } : {}),
  };

  // Does NOT touch `initialState` — Base (no override) stays the default unless
  // explicitly pinned. (See addState.)
  const next: PropertiesData = {
    ...data,
    states: { ...existingStates, [resolvedName]: cloned },
  };

  return { data: next, name: resolvedName };
}

// ---------------------------------------------------------------------------
// renameState
// ---------------------------------------------------------------------------

/**
 * Renames a key in the states map. Updates `initialState` if it pointed at
 * `oldName`. Returns input unchanged if `oldName` is missing or `newName`
 * already exists.
 */
export function renameState(
  data: PropertiesData,
  oldName: string,
  newName: string,
): PropertiesData {
  const existingStates = data.states ?? {};

  if (!(oldName in existingStates)) return data;
  if (newName in existingStates) return data;

  // Rebuild with preserved insertion order
  const nextStates: Record<string, StateDefinition> = {};
  for (const key of Object.keys(existingStates)) {
    nextStates[key === oldName ? newName : key] = existingStates[key];
  }

  return {
    ...data,
    states: nextStates,
    ...(data.initialState === oldName ? { initialState: newName } : {}),
  };
}

// ---------------------------------------------------------------------------
// deleteState
// ---------------------------------------------------------------------------

/**
 * Removes a state. With "Base is not a state", every stored entry is a real,
 * deletable state — there is no undeletable base entry and no special dissolve
 * case (deleting the last state simply leaves an empty `states` map, which IS
 * the stateless / Base-only condition).
 *
 * Rules:
 * - `name` missing → input unchanged.
 * - Remove the key. If it was the pinned `initialState`, clear the pin so the
 *   on-load default reverts to Base (no override).
 *
 * (Historically this REFUSED to delete the base entry and DISSOLVED when only
 * the base would remain; both are obsolete now that Base is implicit.)
 */
export function deleteState(data: PropertiesData, name: string): PropertiesData {
  const existingStates = data.states ?? {};

  if (!(name in existingStates)) return data;

  const nextStates: Record<string, StateDefinition> = {};
  for (const key of Object.keys(existingStates)) {
    if (key !== name) nextStates[key] = existingStates[key];
  }

  return {
    ...data,
    states: nextStates,
    // Deleting the pinned default reverts the on-load default to Base.
    ...(data.initialState === name ? { initialState: undefined } : {}),
  };
}

// ---------------------------------------------------------------------------
// setStateValue
// ---------------------------------------------------------------------------

/**
 * Sets `states[stateName].values[prop]` to `value`. Returns input unchanged
 * if `stateName` is not present.
 */
export function setStateValue(
  data: PropertiesData,
  stateName: string,
  prop: string,
  value: PropertyValue,
): PropertiesData {
  const existingStates = data.states ?? {};

  if (!(stateName in existingStates)) return data;

  const state = existingStates[stateName];

  return {
    ...data,
    states: {
      ...existingStates,
      [stateName]: {
        ...state,
        values: { ...state.values, [prop]: value },
      },
    },
  };
}

// ---------------------------------------------------------------------------
// removeStateValue
// ---------------------------------------------------------------------------

/**
 * Deletes a key from `states[stateName].values` (reset-to-base). Returns
 * input unchanged if `stateName` is not present.
 */
export function removeStateValue(
  data: PropertiesData,
  stateName: string,
  prop: string,
): PropertiesData {
  const existingStates = data.states ?? {};

  if (!(stateName in existingStates)) return data;

  const state = existingStates[stateName];
  const { [prop]: _removed, ...remainingValues } = state.values;

  return {
    ...data,
    states: {
      ...existingStates,
      [stateName]: { ...state, values: remainingValues },
    },
  };
}

// ---------------------------------------------------------------------------
// setStateTransform
// ---------------------------------------------------------------------------

/**
 * Sets or clears `states[stateName].transform`. Returns input unchanged if
 * `stateName` is not present.
 */
export function setStateTransform(
  data: PropertiesData,
  stateName: string,
  transform: TransformOverride | undefined,
): PropertiesData {
  const existingStates = data.states ?? {};

  if (!(stateName in existingStates)) return data;

  const state = existingStates[stateName];
  const { transform: _old, ...rest } = state;

  return {
    ...data,
    states: {
      ...existingStates,
      [stateName]: transform !== undefined ? { ...rest, transform } : { ...rest },
    },
  };
}

// ---------------------------------------------------------------------------
// setStateActive
// ---------------------------------------------------------------------------

/**
 * Sets or clears `states[stateName].active` — the per-state object visibility
 * override (enable/disable when the state is active). `undefined` clears it
 * (omitted = no change). Returns input unchanged if `stateName` is not present.
 */
export function setStateActive(
  data: PropertiesData,
  stateName: string,
  active: boolean | undefined,
): PropertiesData {
  const existingStates = data.states ?? {};

  if (!(stateName in existingStates)) return data;

  const state = existingStates[stateName];
  const { active: _old, ...rest } = state;

  return {
    ...data,
    states: {
      ...existingStates,
      [stateName]: active !== undefined ? { ...rest, active } : { ...rest },
    },
  };
}

// ---------------------------------------------------------------------------
// setStateTransition
// ---------------------------------------------------------------------------

/**
 * Sets or clears `states[stateName].transition`. Returns input unchanged if
 * `stateName` is not present.
 */
export function setStateTransition(
  data: PropertiesData,
  stateName: string,
  transition: TransitionConfig | undefined,
): PropertiesData {
  const existingStates = data.states ?? {};

  if (!(stateName in existingStates)) return data;

  const state = existingStates[stateName];
  const { transition: _old, ...rest } = state;

  return {
    ...data,
    states: {
      ...existingStates,
      [stateName]: transition !== undefined ? { ...rest, transition } : { ...rest },
    },
  };
}

// ---------------------------------------------------------------------------
// ensureDeclaration
// ---------------------------------------------------------------------------

/**
 * Appends `decl` to `data.properties` if no declaration with the same `name`
 * exists. Returns input unchanged (same reference) if it already exists.
 */
export function ensureDeclaration(
  data: PropertiesData,
  decl: PropertyDeclaration,
): PropertiesData {
  if (data.properties.some(p => p.name === decl.name)) return data;

  return {
    ...data,
    properties: [...data.properties, decl],
  };
}

// ---------------------------------------------------------------------------
// setDeclarationValue
// ---------------------------------------------------------------------------

/**
 * Updates an existing declaration's DEFAULT `value`. This is how a base-state
 * edit changes a property whose Renderer field is bound to `{name}`: the base
 * holds no state override, so the resolver falls back to this default. Returns
 * input unchanged (same reference) when no declaration named `name` exists.
 */
export function setDeclarationValue(
  data: PropertiesData,
  name: string,
  value: PropertyValue,
): PropertiesData {
  const idx = data.properties.findIndex(p => p.name === name);
  if (idx < 0) return data;

  const nextProps = data.properties.slice();
  // `PropertyDeclaration` is a discriminated union on `type`; we only swap the
  // default `value` and the caller passes a value matching the existing type.
  nextProps[idx] = { ...nextProps[idx], value } as PropertyDeclaration;

  return { ...data, properties: nextProps };
}

// ---------------------------------------------------------------------------
// freezeStatesForDeclaration
// ---------------------------------------------------------------------------

/**
 * Ensure `decl` exists, and — ONLY when it was newly added — pin every
 * pre-existing state that lacks its own override for `decl.name` to the
 * declaration's default `value` (the "first-bind freeze").
 *
 * Why: binding a Renderer field to `{decl.name}` makes that field
 * state-swappable. States authored BEFORE the bind carry no `values[name]`
 * entry, so the resolver would let them follow the declaration default (Base)
 * retroactively. Freezing them to the current Base value at bind time decouples
 * them, so a later Base edit updates only the declaration and leaves earlier
 * states intact (model-(B) isolation, but deferred to first authoring).
 *
 * The caller writes the user's actual value into the TARGET state afterwards
 * (via `captureStateValue`/`setStateValue`); this only handles the siblings.
 * When the declaration already existed the freeze is skipped — those states
 * were already reconciled at first bind. Pure data transform.
 */
export function freezeStatesForDeclaration(
  data: PropertiesData,
  decl: PropertyDeclaration & { value: PropertyValue },
): PropertiesData {
  const hadDecl = data.properties.some(p => p.name === decl.name);
  let next = ensureDeclaration(data, decl);
  if (hadDecl) return next;

  for (const stateName of Object.keys(next.states ?? {})) {
    if (Object.hasOwn(next.states?.[stateName]?.values ?? {}, decl.name)) continue;
    next = setStateValue(next, stateName, decl.name, decl.value);
  }
  return next;
}
